import { DownloadLink } from "../DownloadLink";
import { useEffect, useRef } from "react";
import { useInView, useReducedMotion } from "framer-motion";
import { useLocation } from "react-router-dom";
import { downloadPath } from "../../routes";
import adit from "../../assets/flow/pms-adit.png";
import carestack from "../../assets/flow/pms-carestack.png";
import cloud9 from "../../assets/flow/pms-cloud9.png";
import curve from "../../assets/flow/pms-curve-dental.png";
import dentaltap from "../../assets/flow/pms-dentaltap.png";
import denticon from "../../assets/flow/pms-denticon.png";
import dentimax from "../../assets/flow/pms-dentimax.png";
import dentos from "../../assets/flow/pms-dentos.png";
import dentrix from "../../assets/flow/pms-dentrix-ascend.png";
import eaglesoft from "../../assets/flow/pms-eaglesoft.png";
import openDental from "../../assets/flow/pms-open-dental.png";
import oryx from "../../assets/flow/pms-oryx.png";
import sensei from "../../assets/flow/pms-sensei-cloud.png";
import sift from "../../assets/flow/pms-siftdental.png";
import tab32 from "../../assets/flow/pms-tab32.png";
import { projectPmsLogo } from "./pmsSphereGeometry";

const pmsLogos = [
  { name: "Dentrix Ascend", src: dentrix },
  { name: "Open Dental", src: openDental },
  { name: "CareStack", src: carestack },
  { name: "Dentaltap", src: dentaltap },
  { name: "Curve Dental", src: curve },
  { name: "DentOS", src: dentos },
  { name: "DentiMax", src: dentimax },
  { name: "tab32", src: tab32 },
  { name: "Sensei Cloud", src: sensei },
  { name: "Adit", src: adit },
  { name: "Eaglesoft", src: eaglesoft },
  { name: "Denticon", src: denticon },
  { name: "Oryx", src: oryx },
  { name: "Cloud 9", src: cloud9 },
  { name: "SiftDental", src: sift },
];

function PmsLogoSphere() {
  const sphereRef = useRef<HTMLDivElement>(null);
  const logoRefs = useRef<Array<HTMLDivElement | null>>([]);
  const angle = useRef(0);
  const inView = useInView(sphereRef, { amount: 0.15 });
  const reducedMotion = useReducedMotion();

  useEffect(() => {
    if (!inView || reducedMotion) return;
    let frame = 0;
    let previous = 0;
    function rotate(time: number) {
      if (previous) angle.current += Math.min(time - previous, 64) * 0.00009;
      previous = time;
      logoRefs.current.forEach((node, index) => {
        if (!node) return;
        const point = projectPmsLogo(index, pmsLogos.length, angle.current);
        node.style.left = `${point.x}%`;
        node.style.top = `${point.y}%`;
        node.style.transform = `translate(-50%, -50%) scale(${point.scale})`;
        node.style.opacity = `${point.opacity}`;
        node.style.zIndex = `${point.zIndex}`;
      });
      frame = requestAnimationFrame(rotate);
    }
    function syncVisibility() {
      cancelAnimationFrame(frame);
      previous = 0;
      if (document.visibilityState === "visible") frame = requestAnimationFrame(rotate);
    }
    syncVisibility();
    document.addEventListener("visibilitychange", syncVisibility);
    return () => {
      cancelAnimationFrame(frame);
      document.removeEventListener("visibilitychange", syncVisibility);
    };
  }, [inView, reducedMotion]);

  return (
    // Keep the animated logo depths local so they cannot overlap navigation overlays.
    <div data-pms-sphere-surface="transparent" className="relative isolate z-0 bg-transparent">
      <div ref={sphereRef} className="relative mx-auto aspect-square w-full max-w-[470px]">
        <div role="img" aria-label="PMS logos arranged in a rotating sphere" className="absolute inset-0">
          <svg aria-hidden="true" viewBox="0 0 470 470" className="absolute inset-0 h-full w-full fill-none stroke-[#eeeaf2]" strokeWidth="0.8">
            <circle cx="235" cy="235" r="165" />
            <ellipse cx="235" cy="235" rx="165" ry="65" transform="rotate(-12 235 235)" />
            <ellipse cx="235" cy="235" rx="75" ry="165" transform="rotate(-12 235 235)" />
          </svg>
          {pmsLogos.map(({ name, src }, index) => {
            const point = projectPmsLogo(index, pmsLogos.length, 0);
            return (
              <div key={name} ref={(node) => { logoRefs.current[index] = node; }} data-pms-sphere-logo={name}
                className="absolute flex h-[11%] w-[24%] items-center justify-center rounded-xl border border-[#e7e9ee] bg-white px-[2.5%] py-[1.8%] shadow-[0_4px_12px_rgba(33,42,57,.035)]"
                style={{ left: `${point.x}%`, top: `${point.y}%`, opacity: point.opacity, zIndex: point.zIndex, transform: `translate(-50%, -50%) scale(${point.scale})` }}>
                <img src={src} alt="" loading="lazy" draggable={false} className="h-full w-full object-contain" />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export function DentalSystemIntegrations() {
  const { pathname, search } = useLocation();

  return (
    <section id="dental-integrations" aria-labelledby="dental-integrations-title" className="scroll-mt-24 bg-[#fbfaf6] px-5 py-8 sm:px-10 sm:py-10 lg:px-12">
      <div className="dental-content relative">
        <header className="text-center">
          <h2 id="dental-integrations-title" className="mx-auto max-w-[920px] text-balance text-[30px] font-extrabold leading-[1.12] tracking-[-.048em] text-[#172033] sm:text-[38px]">
            Your systems. Your workflows. Seamlessly connected.
          </h2>
        </header>
        <div className="mt-6 grid items-center gap-8 lg:grid-cols-[1.05fr_1fr] lg:gap-10">
          <div className="order-2 mx-auto w-full max-w-[500px] lg:order-1">
            <PmsLogoSphere />
          </div>
          <div className="order-1 min-w-0 lg:order-2">
            <h3 className="text-[23px] font-extrabold leading-[1.2] tracking-[-.035em] text-[#172033]">
              Built around your practice.
            </h3>
            <p className="mt-4 text-[14px] leading-[1.85] text-[#667280]">
              Keep the PMS your team already knows. Tell ClawEase what you want to connect, and let AI build the integration around your workflow.
            </p>
            <div className="mt-6">
              <h4 className="text-[15px] font-semibold text-[#293344]">Any workflow. Just ask AI.</h4>
              <p className="mt-1 text-[14px] leading-6 text-[#667280]">From patient intake to appointment updates, connect the steps your practice needs through its available tools and permissions.</p>
            </div>
            <div className="mt-6">
              <h4 className="text-[15px] font-semibold text-[#293344]">Prefer Google Calendar?</h4>
              <p className="mt-1 text-[14px] leading-6 text-[#667280]">Book appointments directly with built-in Google Calendar support. No PMS required.</p>
            </div>
            <DownloadLink
              to={downloadPath("en", "dental_integrations", undefined, search, pathname)}
              className="mt-7 inline-flex min-h-12 max-w-full items-center justify-center rounded-[12px] px-6 py-3 text-center text-[13px] font-extrabold text-white no-underline shadow-[0_14px_32px_rgba(93,49,191,.19)] transition duration-200 hover:-translate-y-0.5 hover:shadow-[0_18px_38px_rgba(93,49,191,.24)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#6d35cf]/55 focus-visible:ring-offset-2 motion-reduce:transform-none motion-reduce:transition-none"
              style={{ background: "linear-gradient(135deg, #5d2cc3 0%, #7834cf 58%, #d638c9 100%)" }}
            >
              Learn more about ClawEase
            </DownloadLink>
          </div>
        </div>
      </div>
    </section>
  );
}
